import {
  frozenKeys,
  hiddenSet,
  laidOutColumns,
  MAX_COLUMN_KEYS,
  MAX_FROZEN_COLUMNS,
  presetForStorage,
  type ColumnIdentity,
  type TableColumnPreset
} from './table-columns.ts'

/** The reserved URL parameter for a shared column layout. */
export const LAYOUT_PARAM = 'cols'

/**
 * Separates the table key from the layout.
 *
 * The first one wins, so a table key must not contain `~`. Column keys are
 * percent-encoded, which is what keeps a `~` inside a key from being read as
 * a second separator.
 */
const TABLE_SEPARATOR = '~'

/**
 * Encode one column key.
 *
 * `encodeURIComponent` leaves `!`, `*` and `~` bare, and each of those is
 * syntax in this value: a leading `!` or `*` is a marker, and `~` splits the
 * table key from the layout. Percent-encode those three after the standard
 * encoding so a key can contain them and still round-trip.
 *
 * @param key - A declared column key.
 * @returns The key as it is written in the layout.
 */
function encodeKey(key: string): string {
  return encodeURIComponent(key).replace(
    /[!~*]/g,
    character => `%${character.charCodeAt(0).toString(16).toUpperCase()}`
  )
}

/**
 * Decode one column key, or null when the encoding is not a key.
 *
 * @param encoded - The key text after its markers.
 * @returns The column key, or null when the percent-encoding is malformed.
 */
function decodeKey(encoded: string): string | null {
  try {
    return decodeURIComponent(encoded)
  } catch {
    return null
  }
}

/**
 * Whether a `cols` value names this table.
 *
 * A value with no `~`, or one whose key is another table, does not.
 *
 * @param value - The raw parameter.
 * @param tableKey - The table asking.
 * @returns True when the value is this table's layout.
 */
export function layoutNamesTable(value: unknown, tableKey: string): boolean {
  return (
    typeof value === 'string' &&
    value.startsWith(`${tableKey}${TABLE_SEPARATOR}`)
  )
}

/**
 * Read one table's shared layout.
 *
 * The value is `<tableKey>~<layout>`. A value for another table, or one with
 * no `~`, is not a layout for this table. Stale, duplicate, and malformed
 * keys are dropped.
 *
 * @param value - The raw `cols` parameter.
 * @param tableKey - The table this layout would apply to.
 * @param columns - The screen's declared columns.
 * @returns The layout, or null when the value is not this table's.
 */
export function parseLayoutParam(
  value: unknown,
  tableKey: string,
  columns: readonly ColumnIdentity[]
): TableColumnPreset | null {
  if (!layoutNamesTable(value, tableKey) || typeof value !== 'string')
    return null
  const layout = value.slice(tableKey.length + TABLE_SEPARATOR.length)
  if (layout.length === 0) return null
  const declared = new Set(columns.map(column => column.key))
  const order: string[] = []
  const hidden: string[] = []
  const frozen: string[] = []
  const seen = new Set<string>()
  for (const token of layout.split(',')) {
    const markers = token.match(/^[!*]*/)?.[0] ?? ''
    const key = decodeKey(token.slice(markers.length))
    if (key === null || !declared.has(key) || seen.has(key)) continue
    seen.add(key)
    order.push(key)
    if (markers.includes('!')) hidden.push(key)
    else if (markers.includes('*') && frozen.length < MAX_FROZEN_COLUMNS)
      frozen.push(key)
    if (order.length >= MAX_COLUMN_KEYS) break
  }
  if (order.length === 0) return null
  return presetForStorage(columns, { order, hidden, frozen })
}

/**
 * Write one table's order, visibility, and frozen state, without widths.
 *
 * @param tableKey - The table the link names. Must not contain `~`.
 * @param columns - The screen's declared columns.
 * @param preset - The layout to write.
 * @returns The `cols` value, `<tableKey>~<layout>`.
 */
export function serializeLayoutParam(
  tableKey: string,
  columns: readonly ColumnIdentity[],
  preset: TableColumnPreset
): string {
  const hidden = hiddenSet(columns, preset)
  const frozen = new Set(frozenKeys(columns, preset))
  const layout = laidOutColumns(columns, preset)
    .slice(0, MAX_COLUMN_KEYS)
    .map(column => {
      const marker = hidden.has(column.key)
        ? '!'
        : frozen.has(column.key)
          ? '*'
          : ''
      return `${marker}${encodeKey(column.key)}`
    })
    .join(',')
  return `${tableKey}${TABLE_SEPARATOR}${layout}`
}
